
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FeaturesConfigModule, GlobalMessageService, GlobalMessageType, I18nModule, Product, ProductSearchPage, TranslationService, WindowRef } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, skip, tap } from 'rxjs/operators';
import { AtMessageModule, ICON_TYPE, IconModule, ItemCounterModule, ListNavigationModule, MediaModule, OutletModule, PageComponentModule, PaginationModule, ProductListModule, SpinnerModule, StarRatingModule, ViewConfig, ViewModes } from '@spartacus/storefront';
import { ActivatedRoute, Router } from '@angular/router';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GaItem, GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { GenProductListComponentService } from 'src/app/generac/services/product-list-component.service';
import { CommonModule } from '@angular/common';
import { SafeModule } from 'src/app/generac/shared/pipes/safe/safe.module';
import { GenSharedModule } from 'src/app/generac/shared/shared.module';
import { GenAddToCartModule } from '../../cart/product-add-to-cart/product-add-to-cart.module';
import { GenProductGridItemComponent } from './product-grid-item/product-grid-item.component';
import { GenDocumentListComponent } from '../document-list/document-list.component';
import { GenProductListItemComponent } from './product-list-item/product-list-item.component';
import { GenInfoMessageComponent } from 'src/app/generac/shared/components/info-message/info-message.component';

export enum SearchPageTabValue {
  ModelsAndParts = 'modelsAndParts',
  ServiceParts = 'serviceParts',
  Documents = 'documents',
}

type SearchPageTab = {
  name: string;
  value: SearchPageTabValue;
};

const SEARCH_PAGE_TABS: SearchPageTab[] = [
  { name: 'Models and Parts', value: SearchPageTabValue.ModelsAndParts },
  { name: 'Parts', value: SearchPageTabValue.ServiceParts },
  { name: 'Documents', value: SearchPageTabValue.Documents },
];

@Component({
  selector: 'gen-product-list',
  templateUrl: './product-list.component.html',
  imports: [
    AtMessageModule,
    CommonModule,
    FeaturesConfigModule,
    I18nModule,
    IconModule,
    ItemCounterModule,
    GenSharedModule,
    ListNavigationModule,
    MediaModule,
    GenProductGridItemComponent,
    OutletModule,
    PageComponentModule,
    SpinnerModule,
    StarRatingModule,
    SafeModule,
    GenAddToCartModule,
    ProductListModule,
    GenDocumentListComponent,
    PaginationModule,
    GenProductListItemComponent,
    GenInfoMessageComponent,
  ],
})
export class GenProductListComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  isInfiniteScroll: boolean | undefined;

  model$: Observable<ProductSearchPage> =
    this.productListComponentService.model$;

  viewMode$ = new BehaviorSubject<ViewModes>(ViewModes.List);
  ViewModes = ViewModes;
  iconTypes = ICON_TYPE;

  tooltipMessage: string;
  phoneOrEmail: string;
  gaItems: GaItem[] = [];
  gaListName: string;
  gaEntrySource: string;
  gaProductCategories: { name: string }[] = [];
  activeTabIndex: number = 0;
  isServicePartsEnabled: boolean = false;
  isSearchPage: boolean;
  searchPageTabs = SEARCH_PAGE_TABS;
  searchBy: string;
  spSearchCode: string;
  searchTerm: string;
  currentQueryParams: any;
  servicePartsTabIndex = this.searchPageTabs.findIndex((tab) => tab.value === SearchPageTabValue.ServiceParts);
  documentsTabIndex = this.searchPageTabs.findIndex((tab) => tab.value === SearchPageTabValue.Documents);
  tooltipPriceMessage: string;
  tooltipPrice: string;

  isTabHidden(tabValue: SearchPageTabValue | string): boolean {
    // Cache boolean checks to avoid repeating comparisons and make logic clearer
    const isModelsAndParts = tabValue === SearchPageTabValue.ModelsAndParts;
    const isServicePartsTab = tabValue === SearchPageTabValue.ServiceParts;
    const isDocumentsTab = tabValue === SearchPageTabValue.Documents;
    const activeIsServiceOrDocuments =
      this.activeTabIndex === this.servicePartsTabIndex ||
      this.activeTabIndex === this.documentsTabIndex;

    if (isModelsAndParts) {
      // Hide ModelsAndParts when there's an explicit searchBy or when a service/docs tab is active
      return !!this.searchBy || activeIsServiceOrDocuments;
    }

    if (isServicePartsTab || isDocumentsTab) {
      // Hide Parts/Documents tabs when no searchBy and neither service nor documents tab is active
      return !this.searchBy && !activeIsServiceOrDocuments;
    }

    return false;
  }

  constructor(
    private productListComponentService: GenProductListComponentService,
    private globalMessageService: GlobalMessageService,
    public scrollConfig: ViewConfig,
    private baseStoreService: GenBaseStoreService,
    protected customerService: GenCustomerService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected winRef: WindowRef,
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    protected translationService: TranslationService
  ) { }

  ngOnInit(): void {
    this.isInfiniteScroll = this.scrollConfig.view?.infiniteScroll?.active;
    this.gaListName = this.setGaListName();
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
    this.isSearchPage = this.winRef.location.pathname.startsWith('/search');

    if (this.isServicePartsEnabled && this.isSearchPage) {
      this.subscription.add(
        this.activatedRoute.queryParams.pipe(
          tap((queryParams) => {
            this.currentQueryParams = queryParams;
            this.searchBy = queryParams?.searchBy;
            this.spSearchCode = queryParams?.code;
            this.searchTerm = this.getSearchTermFromUrl();
            let index;
            let servicePartQueryParam;
            if (queryParams?.query) {
              const querySplitted: Array<string> = queryParams?.query?.split(':');
              servicePartQueryParam = querySplitted[querySplitted.indexOf('servicePart') + 1] === 'true';
            }
            if ((queryParams?.searchBy?.length > 0 && queryParams?.code?.length > 0 || servicePartQueryParam) && !queryParams?.tab) {
              index = 1;
            } else if (queryParams?.tab?.length > 0) {
              index = Number(queryParams?.tab);
            } else {
              index = 0;
            }
            this.activeTabIndex = index;
            if (!queryParams?.tab && this.winRef.location.pathname.startsWith('/search')) {
              this.navigateToSearch();
            }
          })
        ).subscribe()
      )
    }

    this.subscription.add(
      combineLatest([this.model$, this.viewMode$])
        .pipe(
          tap(([model, mode]) => {
            if (model?.products && model?.breadcrumbs) {
              this.gaProductCategories = [];
              if (model?.breadcrumbs) {
                for (const breadcrumb of model.breadcrumbs) {
                  this.gaProductCategories.push({ name: breadcrumb.facetValueName })
                };
              }
              const mappedProducts = model.products.map((product) => {
                return {
                  ...product,
                  categories: this.gaProductCategories
                }
              });

              this.googleAnalyticsService.sendViewItemListEvent(mappedProducts, this.gaListName);
            }
          }),
          skip(1),
          filter(([model, mode]) => !!model && !!mode)
        )
        .subscribe(() =>
          this.globalMessageService.add(
            { key: 'sorting.pageViewUpdated' },
            GlobalMessageType.MSG_TYPE_ASSISTIVE,
            500
          )
        )
    );

    this.subscription.add(
      combineLatest([
        this.baseStoreService.getTooltipMessage(),
        this.baseStoreService.getPhoneNumber(),
      ]).subscribe(([messsage, phoneOrEmail]) => {
        this.tooltipMessage = messsage?.message;
        this.phoneOrEmail = phoneOrEmail?.contactInfo;
      })
    );

    this.subscription.add(
      this.customerService.getB2bUnitChanged().subscribe((isChanged) => {
        if (isChanged) {
          this.productListComponentService.getPageItems(0);
        }
      })
    );

  }

  onPriceTooltipEnter(): void {
    const translationKey = this.activeTabIndex === this.servicePartsTabIndex ? 'productList.tooltipPriceMslp' : 'productList.tooltipPriceMixed';
    this.subscription.add(
      this.translationService.translate(translationKey).subscribe((translated) => {
        this.tooltipPrice = translated;
      })
    );
  }

  getPatchedProduct(product: Product): Product {
    const patch = this.productListComponentService.getFromProductPatchMap(product.code);
    return patch ? { ...product, ...patch } : product;
  }

  private setGaListName(): string {
    const urlParams = new URLSearchParams(this.winRef.location.search);
    this.gaEntrySource = urlParams.get('entry_source');
    const homePagePromoGaList: boolean = urlParams.get('gaListName') === GenGaListNames.HOMEPAGE_BANNER_CAROUSEL;

    if (this.isSearchPage) {
      return GenGaListNames.SEARCH;
    } else if (this.winRef.location.href.indexOf('accessoriesProductRef') > -1) {
      return GenGaListNames.ACCESSORIES;
    } else if (homePagePromoGaList) {
      return GenGaListNames.HOMEPAGE_BANNER_CAROUSEL;
    } else {
      return GenGaListNames.PLP;
    }
  }

  sortList(sortCode: string): void {
    this.productListComponentService.sort(sortCode);
  }

  setViewMode(mode: ViewModes): void {
    this.viewMode$.next(mode);
  }

  selectTab(index: number): void {
    this.activeTabIndex = index;
    this.navigateToSearch(true);
  }

  getSearchTermFromUrl(): string {
    const splittedPath = this.winRef.location.pathname.split('/');
    const searchTerm = decodeURI(splittedPath?.length > 2 ? splittedPath[splittedPath?.length - 1] : '');
    return decodeURIComponent(searchTerm);
  }

  navigateToSearchWithExtraParams(freeTextSearch: string) {
    const splittedQuery = this.currentQueryParams.query.split(':');
    splittedQuery[0] = freeTextSearch;
    const query = this.currentQueryParams.query?.startsWith(':') ? (freeTextSearch + this.currentQueryParams.query) : splittedQuery.join(':');;
    this.router.navigate([`/search/${encodeURIComponent(freeTextSearch)}`], { queryParams: { ...this.currentQueryParams, query } });
  }

  navigateToSearch(initState?: boolean): void {
    this.productListComponentService.navigateToSearch(this.activeTabIndex, this.getSearchTermFromUrl(), initState);
  }

  ngOnDestroy(): void {
    this.productListComponentService.clearProductPatchMap();
    this.subscription.unsubscribe();
  }
}
